import React from "react";
import Product from "./product";
import useSessionStorage from "shared/hooks/session-storage"
import OrderSwitcher from './filters/order'
import Advert from 'shared/advert.jsx'
import ProductMap, { hasPoint } from './product-map'

const List = React.forwardRef(({ layout, config, results, order, changeOrder, showMap, toggleMap, compared, near, clearNear }, ref ) => {

	const [ visibleProducts, setVisibleProducts ] = useSessionStorage( config.path, config.loadMore.active ? parseInt(config.loadMore.perPage) : 0 );

	// The map only ever shows what the list is showing, so Load more grows both.
	const shown    = config.loadMore.active ? results.products.slice( 0, visibleProducts ) : results.products
	const pinned   = shown.filter( hasPoint )
	const unpinned = shown.filter( product => !hasPoint( product ) )

	// The only thing lit on both sides, and it is not a mouse event: the products the
	// concierge measured for a distance answer.
	const lit = compared || []

	// Was a useEffect inside this function, which only ever assigned to a local
	// after the render that read it — and a hook in here breaks the moment the
	// unmapped group renders it a second time. Plain expression, guarded for the
	// server-side prerender.
	const scrollToProductNumber = typeof window !== 'undefined' && window.innerWidth <= 630 ? 8 : 12

	const renderProducts = ( products, withScrollTarget ) => {

		return products.map((product, index) => (
			<Product
				key={product.id}
				product={product}
				cdnPath={config.cdnPath}
				path={config.path}
				promoted={config.allowPromoted}
				linkQuery={config.linkQuery}
				scrollTarget={withScrollTarget && ( !config.loadMore.active || config.loadMore.perPage <= visibleProducts ) && index == scrollToProductNumber ? true : false}
				highlighted={lit.includes( product.id )}
				ref={ref}
			/>
		));
	};

	// Same card layout with the map open or closed — the map is a block above the
	// results, not a side panel competing with them for width.
	const gridClass = `grid-x grid-padding-x small-up-1 medium-up-2 large-up-3 xlarge-up-4 product-grid ${layout}-view`

	return (
		<div>
			<div className="grid-x grid-padding-x align-middle align-justify">
				<div className="cell auto">
					<h4 className="results-count">
						{results.products.length} activities found in {config.title}
						{near && <span> near {near.label}</span>}
					</h4>
					{/* Without a way out of it the guest is stuck looking at one
					    neighbourhood with no idea why the rest vanished. */}
					{near &&
						<div className="remove-filter border w-fit rounded">
							Within {near.miles} miles of {near.label}
							<a onClick={clearNear}><span className="icon icon-cross-plain small black"></span></a>
						</div>
					}
				</div>
				<div className="cell shrink">
					{pinned.length > 0 && showMap && (
						<button type="button" className="map-toggle" onClick={toggleMap}>
							<span className="icon icon-location mg-r-sm"></span>
							Hide map
						</button>
					)}
					<OrderSwitcher
						order={order}
						changeOrder={changeOrder}
					/>
				</div>
			</div>

			{/* The map is always on the page. Collapsed it is a half-height teaser under
			    a full-size View map button — every tap expands, none reach the map, so
			    scrolling past can't pan it. Hide map returns to the teaser, not to
			    nothing. The key remounts the map on toggle so it refits to the new
			    height instead of keeping the teaser's viewport. */}
			{pinned.length > 0 && (
				<>
					<div className={`activity-map activity-map--panel${showMap ? '' : ' activity-map--peek'}`}>
						<ProductMap
							key={showMap ? 'open' : 'peek'}
							products={pinned}
							cdnPath={config.cdnPath}
							path={config.path}
							linkQuery={config.linkQuery}
							highlighted={lit}
						/>
						{!showMap && (
							<button type="button" className="activity-map__peek-button" onClick={toggleMap}>
								<span className="activity-map__peek-pill">
									<span className="icon icon-location mg-r-sm"></span>
									View map
								</span>
							</button>
						)}
					</div>
					{showMap && <p className="map-count">Showing {pinned.length} of {shown.length} on the map.</p>}
				</>
			)}

			<div className={gridClass}>
				{renderProducts( showMap ? pinned : shown, true )}
			</div>

			{showMap && unpinned.length > 0 && (
				<>
					<div className="divider small"></div>
					<h4 className="results-count">Venue arranged with you ({unpinned.length})</h4>
					<p className="map-count">These operators will book a venue near you, or come to your own venue if you have one.</p>
					<div className={gridClass}>
						{renderProducts( unpinned, false )}
					</div>
				</>
			)}

			{config.loadMore.active && visibleProducts < results.products.length && (
				<div className="grid-x grid-padding-x align-center">
					<div className="cell medium-6 text-center">
						<button
							onClick={() => {
								setVisibleProducts( parseInt( visibleProducts + config.loadMore.perPage ) );
							}}
							type="button"
							className="load-more"
						>
							Load more
						</button>
					</div>
				</div>
			)}
			{config.advert ? (
				<div className="hide-for-large mg-v-lrg">
					<Advert />
				</div>
			) : null}
		</div>
	);
})

export default List;
